import { createStore, produce, unwrap } from 'solid-js/store'
import { createMemo } from 'solid-js'
import { BALANCE, MODELS, UPGRADES, HARDWARE, ModelDef, UpgradeDef, HardwareDef, DebugEra } from '../config/balanceConfig'
import { log, eventLog } from './eventLog'
import { toast } from './toastStore'
import { SaveSystem } from '../systems/SaveSystem'
import { Analytics } from '../systems/AnalyticsSystem'

// Helper to save after actions
const saveGame = () => {
  SaveSystem.save(JSON.stringify(unwrap(state)))
}

// Per-model state
export interface ModelState {
  quality: number
  users: number
}

export interface GameState {
  // Core resources
  money: number
  energy: number
  compute: number
  research: number

  // Hardware owned (list of hardware IDs)
  ownedHardware: string[]

  // Models - each has its own quality and users
  models: Record<string, ModelState>
  
  // Current model being trained
  currentModelIndex: number
  unlockedModels: string[]

  // Upgrades owned
  upgrades: Record<string, number>

  // Stats
  totalComputeSpent: number
  totalInferences: number
  playTime: number
  startTime: number

  // Pricing
  price: number
  
  // Discontinuation system: when a model with discontinuesOlderModels is trained,
  // this stores the model ID that triggered discontinuation
  discontinuationActiveFrom?: string
  
  // Victory: triggered when ASI is trained
  victoryAchieved: boolean
  victoryTime?: number // playTime when victory was achieved
  
  // Auto-inference throttle (0-100, percentage of max rate to use)
  autoInferenceThrottle: number
  // Auto-train throttle (0-100, percentage of max rate to use)
  autoTrainThrottle: number
  // Auto-research throttle (0-100, percentage of max rate to use)
  autoResearchThrottle: number
  // Game paused
  paused: boolean
}

const createInitialModels = (): Record<string, ModelState> => {
  const models: Record<string, ModelState> = {}
  models[MODELS[0].id] = { quality: BALANCE.START_MODEL_QUALITY, users: BALANCE.START_USERS }
  return models
}

const initialState: GameState = {
  money: BALANCE.START_MONEY,
  energy: BALANCE.START_ENERGY,
  compute: BALANCE.START_COMPUTE,
  research: 0,

  ownedHardware: ['laptop'], // Start with basic laptop

  models: createInitialModels(),
  currentModelIndex: 0,
  unlockedModels: [MODELS[0].id],

  upgrades: {},

  totalComputeSpent: 0,
  totalInferences: 0,
  playTime: 0,
  startTime: Date.now(),

  price: BALANCE.START_PRICE,
  
  victoryAchieved: false,
  
  autoInferenceThrottle: 100, // 100% = full speed
  autoTrainThrottle: 100,
  autoResearchThrottle: 100,
  paused: false,
}

// Create the store
const [state, setState] = createStore<GameState>({ ...initialState })

// Track if we've shown the compute limited warning
let computeLimitedWarningShown = false

// Get all owned hardware definitions
export const ownedHardwareList = createMemo((): HardwareDef[] => {
  return state.ownedHardware
    .map(id => HARDWARE.find(h => h.id === id))
    .filter((h): h is HardwareDef => h !== undefined)
})

// Best hardware (for display)
export const bestHardware = createMemo((): HardwareDef => {
  const owned = ownedHardwareList()
  if (owned.length === 0) return HARDWARE[0]
  return owned.reduce((best, hw) => hw.computePerSec > best.computePerSec ? hw : best)
})

// Total HW power from all owned hardware + boosts
export const totalHwPower = createMemo(() => {
  const baseHwPower = ownedHardwareList().reduce((sum, hw) => sum + hw.computePerSec, 0)
  
  // Add hw_boost upgrades
  let boost = 0
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'hw_boost') {
      boost += upgrade.effect.value * count
    }
  }
  
  return baseHwPower + boost
})

// Total energy consumption from all owned hardware
export const totalHwEnergyUse = createMemo(() => {
  return ownedHardwareList().reduce((sum, hw) => sum + hw.energyPerSec, 0)
})

// Computed values from upgrades
export const maxEnergy = createMemo(() => {
  let total = BALANCE.START_MAX_ENERGY
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'max_energy') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

export const energyRegen = createMemo(() => {
  let total = BALANCE.START_ENERGY_REGEN
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'energy_regen') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

export const maxCompute = createMemo(() => {
  let total = BALANCE.START_MAX_COMPUTE
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'max_compute') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

export const trainEfficiency = createMemo(() => {
  let total = 1
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'train_efficiency') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

export const researchEfficiency = createMemo(() => {
  let total = 1
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'research_efficiency') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

// Auto-inference rate (inferences per second)
export const autoInferenceRate = createMemo(() => {
  let total = 0
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'auto_inference') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

// Auto-train rate (trains per second)
export const autoTrainRate = createMemo(() => {
  let total = 0
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'auto_train') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

// Auto-research rate (research actions per second)
export const autoResearchRate = createMemo(() => {
  let total = 0
  for (const upgrade of UPGRADES) {
    const count = state.upgrades[upgrade.id] || 0
    if (count > 0 && upgrade.effect.type === 'auto_research') {
      total += upgrade.effect.value * count
    }
  }
  return total
})

export const currentModel = createMemo((): ModelDef => {
  const idx = state.currentModelIndex
  if (idx < 0 || idx >= MODELS.length) return MODELS[0]
  return MODELS[idx]
})

export const currentModelState = createMemo((): ModelState => {
  const model = currentModel()
  return state.models[model.id] || { quality: 0, users: 0 }
})

// Best unlocked model index (for scaling research costs)
export const bestUnlockedModelIndex = createMemo(() => {
  let bestIndex = 0
  for (const modelId of state.unlockedModels) {
    const idx = MODELS.findIndex(m => m.id === modelId)
    if (idx > bestIndex) bestIndex = idx
  }
  return bestIndex
})

// Research cost scales with best unlocked model
// Base cost * (2 ^ modelIndex) - so it doubles for each model tier
export const researchComputeCost = createMemo(() => {
  const modelIndex = bestUnlockedModelIndex()
  return BALANCE.RESEARCH_COMPUTE_COST * Math.pow(2, modelIndex)
})

export const researchEnergyCost = createMemo(() => {
  const modelIndex = bestUnlockedModelIndex()
  return BALANCE.RESEARCH_ENERGY_COST * Math.pow(2, modelIndex)
})

// Total users across all models
export const totalUsers = createMemo(() => {
  let total = 0
  for (const modelId of state.unlockedModels) {
    const modelState = state.models[modelId]
    if (modelState) {
      total += modelState.users
    }
  }
  return total
})

// Market price - weighted average of unlocked models' market prices
export const marketPrice = createMemo(() => {
  if (state.unlockedModels.length === 0) return MODELS[0].marketPricePerMillion
  
  // Use the best (most advanced) unlocked model's market price as reference
  let bestModelIndex = 0
  for (const modelId of state.unlockedModels) {
    const idx = MODELS.findIndex(m => m.id === modelId)
    if (idx > bestModelIndex) bestModelIndex = idx
  }
  return MODELS[bestModelIndex].marketPricePerMillion
})

// Revenue per inference (for display)
export const revenuePerInference = createMemo(() => {
  let total = 0
  for (const modelId of state.unlockedModels) {
    const modelState = state.models[modelId]
    const modelDef = MODELS.find(m => m.id === modelId)
    if (modelState && modelDef && modelState.users > 0) {
      const qualityMultiplier = modelState.quality < BALANCE.CRITICAL_QUALITY ? 0.5 : 1
      const revenuePerUser = (state.price * modelDef.tokensPerTask / 1_000_000) * qualityMultiplier
      total += revenuePerUser * modelState.users
    }
  }
  return total
})

// Check if a model is discontinued (can't be trained anymore)
export const isModelDiscontinued = (modelId: string): boolean => {
  if (!state.discontinuationActiveFrom) return false
  
  // Find the index of the model that triggered discontinuation
  const discontinuationModelIndex = MODELS.findIndex(m => m.id === state.discontinuationActiveFrom)
  if (discontinuationModelIndex === -1) return false
  
  // Find the index of the model we're checking
  const modelIndex = MODELS.findIndex(m => m.id === modelId)
  if (modelIndex === -1) return false
  
  // All models before the discontinuation model are discontinued
  return modelIndex < discontinuationModelIndex
}

// Actions
export const actions = {
  getTrainCost: () => MODELS[state.currentModelIndex].trainComputeCost,
  
  canAffordTrain: () => {
    const model = MODELS[state.currentModelIndex]
    // Can't train discontinued models
    if (isModelDiscontinued(model.id)) return false
    return state.compute >= actions.getTrainCost() - 0.001 && state.energy >= BALANCE.TRAIN_ENERGY_COST - 0.001
  },
  
  clickTrain: () => {
    if (!actions.canAffordTrain()) return false
    
    const trainCost = actions.getTrainCost()
    const model = MODELS[state.currentModelIndex]
    const efficiency = trainEfficiency()
    
    setState(produce(s => {
      s.compute -= trainCost
      s.energy -= BALANCE.TRAIN_ENERGY_COST
      s.totalComputeSpent += trainCost
      
      // Ensure model state exists
      if (!s.models[model.id]) {
        s.models[model.id] = { quality: 0, users: 0 }
      }
      
      // Quality gain with efficiency multiplier (asymptotic towards max)
      const maxQuality = model.maxQuality
      const currentQuality = s.models[model.id].quality
      const qualityRoom = maxQuality - currentQuality
      const baseGain = BALANCE.BASE_QUALITY_PER_TRAIN * efficiency
      const asymptoticGain = baseGain * (qualityRoom / maxQuality)
      s.models[model.id].quality = Math.min(maxQuality, currentQuality + asymptoticGain)
      
      // Check if this model triggers discontinuation of older models
      if (model.discontinuesOlderModels && !s.discontinuationActiveFrom) {
        s.discontinuationActiveFrom = model.id
        log.milestone(`${model.name} released! All previous models are being discontinued. Users will migrate gradually.`, s.playTime)
      }
      
      // Check if this model triggers victory (ASI)
      if (model.triggersVictory && !s.victoryAchieved) {
        s.victoryAchieved = true
        s.victoryTime = s.playTime
        log.milestone(`THE SINGULARITY IS HERE! ${model.name} has transcended human intelligence.`, s.playTime)
      }
    }))
    
    checkTutorials(state)
    saveGame()
    return true
  },

  canInfer: () => totalUsers() >= 1 && state.compute >= BALANCE.INFERENCE_COMPUTE_COST && state.energy >= BALANCE.INFERENCE_ENERGY_COST,

  clickInference: () => {
    if (!actions.canInfer()) return false
    
    setState(produce(s => {
      s.compute -= BALANCE.INFERENCE_COMPUTE_COST
      s.energy -= BALANCE.INFERENCE_ENERGY_COST
      s.totalComputeSpent += BALANCE.INFERENCE_COMPUTE_COST
      
      // Calculate revenue from ALL unlocked models
      // Revenue = (price per 1M tokens) * (tokens per task) / 1M * users
      let totalRevenue = 0
      for (const modelId of s.unlockedModels) {
        const modelState = s.models[modelId]
        const modelDef = MODELS.find(m => m.id === modelId)
        if (modelState && modelDef && modelState.users > 0) {
          const qualityMultiplier = modelState.quality < BALANCE.CRITICAL_QUALITY ? 0.5 : 1
          const revenuePerUser = (s.price * modelDef.tokensPerTask / 1_000_000) * qualityMultiplier
          totalRevenue += revenuePerUser * modelState.users
        }
      }
      s.money += totalRevenue
      s.totalInferences++
    }))
    saveGame()
    return true
  },

  isResearchUnlocked: () => state.totalComputeSpent >= BALANCE.RESEARCH_UNLOCK_THRESHOLD || state.research > 0,
  
  getResearchComputeCost: () => researchComputeCost(),
  getResearchEnergyCost: () => researchEnergyCost(),
  
  canAffordResearch: () => state.compute >= researchComputeCost() - 0.001 && state.energy >= researchEnergyCost() - 0.001,

  clickResearch: () => {
    if (!actions.isResearchUnlocked() || !actions.canAffordResearch()) return false
    
    const efficiency = researchEfficiency()
    const computeCost = researchComputeCost()
    const energyCost = researchEnergyCost()
    
    setState(produce(s => {
      s.compute -= computeCost
      s.energy -= energyCost
      s.totalComputeSpent += computeCost
      s.research += 1 * efficiency
    }))
    checkTutorials(state)
    saveGame()
    return true
  },

  increasePrice: () => {
    setState('price', p => Math.min(BALANCE.MAX_PRICE, p * 1.1))
    saveGame()
  },

  decreasePrice: () => {
    setState('price', p => Math.max(BALANCE.MIN_PRICE, p / 1.1))
    saveGame()
  },

  increaseAutoThrottle: () => {
    setState('autoInferenceThrottle', t => Math.min(100, t + 10))
    saveGame()
  },

  decreaseAutoThrottle: () => {
    setState('autoInferenceThrottle', t => Math.max(0, t - 10))
    saveGame()
  },

  increaseAutoTrainThrottle: () => {
    setState('autoTrainThrottle', t => Math.min(100, t + 10))
    saveGame()
  },

  decreaseAutoTrainThrottle: () => {
    setState('autoTrainThrottle', t => Math.max(0, t - 10))
    saveGame()
  },

  increaseAutoResearchThrottle: () => {
    setState('autoResearchThrottle', t => Math.min(100, t + 10))
    saveGame()
  },

  decreaseAutoResearchThrottle: () => {
    setState('autoResearchThrottle', t => Math.max(0, t - 10))
    saveGame()
  },

  togglePause: () => {
    setState('paused', p => !p)
    saveGame()
  },

  getUpgradeCount: (id: string) => state.upgrades[id] || 0,

  getUpgradeCost: (upgrade: UpgradeDef) => {
    const owned = state.upgrades[upgrade.id] || 0
    const multiplier = upgrade.costMultiplier ?? BALANCE.UPGRADE_COST_MULTIPLIER
    return Math.floor(upgrade.baseCost * Math.pow(multiplier, owned))
  },

  canAffordUpgrade: (upgrade: UpgradeDef) => {
    const cost = actions.getUpgradeCost(upgrade)
    return state.money >= cost - 0.001
  },

  isUpgradeAvailable: (upgrade: UpgradeDef) => {
    // Block all upgrades until first model reaches 50% quality
    const firstModel = MODELS[0]
    const firstModelQuality = state.models[firstModel.id]?.quality || 0
    if (firstModelQuality < 50 && state.unlockedModels.length === 1) {
      return false
    }
    
    const owned = state.upgrades[upgrade.id] || 0
    if (upgrade.maxOwned !== -1 && owned >= upgrade.maxOwned) {
      return false
    }
    if (upgrade.requires) {
      if (upgrade.requires.money && state.money < upgrade.requires.money) {
        return false
      }
      if (upgrade.requires.model && !state.unlockedModels.includes(upgrade.requires.model)) {
        return false
      }
      if (upgrade.requires.upgrade && !state.upgrades[upgrade.requires.upgrade]) {
        return false
      }
    }
    return true
  },

  buyUpgrade: (upgrade: UpgradeDef) => {
    if (!actions.isUpgradeAvailable(upgrade) || !actions.canAffordUpgrade(upgrade)) {
      return false
    }

    const cost = actions.getUpgradeCost(upgrade)
    
    setState(produce(s => {
      s.money -= cost
      s.upgrades[upgrade.id] = (s.upgrades[upgrade.id] || 0) + 1
      
      // If it's a hardware upgrade, add to owned hardware
      if (upgrade.effect.type === 'hw_upgrade' && upgrade.effect.hardwareId) {
        if (!s.ownedHardware.includes(upgrade.effect.hardwareId)) {
          s.ownedHardware.push(upgrade.effect.hardwareId)
        }
      }
    }))
    
    // Track hardware purchases
    if (upgrade.effect.type === 'hw_upgrade' && upgrade.effect.hardwareId) {
      Analytics.hardwarePurchased(upgrade.effect.hardwareId, state.playTime)
    }
    
    log.success(`Purchased ${upgrade.name}!`, state.playTime)
    checkTutorials(state)
    saveGame()
    return true
  },

  // Get sell price for hardware (50% of original cost)
  getHardwareSellPrice: (hardwareId: string): number => {
    const upgrade = UPGRADES.find(u => u.effect.type === 'hw_upgrade' && u.effect.hardwareId === hardwareId)
    if (!upgrade) return 0
    return Math.floor(upgrade.baseCost * 0.5)
  },

  // Check if hardware can be sold (must own it and not be the last one)
  canSellHardware: (hardwareId: string): boolean => {
    // Can't sell laptop (starting hardware)
    if (hardwareId === 'laptop') return false
    // Must own it
    if (!state.ownedHardware.includes(hardwareId)) return false
    // Must have at least 2 hardware items
    if (state.ownedHardware.length <= 1) return false
    return true
  },

  // Check if a model can be discontinued
  canDiscontinueModel: (modelId: string): boolean => {
    // Must be unlocked
    if (!state.unlockedModels.includes(modelId)) return false
    // Must have at least 2 models unlocked
    if (state.unlockedModels.length <= 1) return false
    // Can't discontinue the currently training model
    const currentModelId = MODELS[state.currentModelIndex]?.id
    if (modelId === currentModelId) return false
    // Can't discontinue the most advanced (last unlocked) model
    // Find the most advanced unlocked model by index in MODELS array
    let maxIndex = -1
    let maxModelId = ''
    for (const id of state.unlockedModels) {
      const idx = MODELS.findIndex(m => m.id === id)
      if (idx > maxIndex) {
        maxIndex = idx
        maxModelId = id
      }
    }
    if (modelId === maxModelId) return false
    return true
  },

  // Discontinue a model - removes it from unlocked, deletes its state
  discontinueModel: (modelId: string): boolean => {
    if (!actions.canDiscontinueModel(modelId)) return false
    
    const modelDef = MODELS.find(m => m.id === modelId)
    
    setState(produce(s => {
      // Remove from unlocked models
      s.unlockedModels = s.unlockedModels.filter(id => id !== modelId)
      // Delete model state (users, quality)
      delete s.models[modelId]
    }))
    
    log.info(`Discontinued ${modelDef?.name || modelId}. Users have migrated to other models.`, state.playTime)
    saveGame()
    return true
  },

  // Sell hardware for 50% of original price
  sellHardware: (hardwareId: string): boolean => {
    if (!actions.canSellHardware(hardwareId)) return false
    
    const sellPrice = actions.getHardwareSellPrice(hardwareId)
    const hw = HARDWARE.find(h => h.id === hardwareId)
    
    setState(produce(s => {
      s.money += sellPrice
      s.ownedHardware = s.ownedHardware.filter(id => id !== hardwareId)
      
      // Also remove the upgrade count so it can be re-purchased
      const upgradeId = `hw-${hardwareId}`
      if (s.upgrades[upgradeId]) {
        delete s.upgrades[upgradeId]
      }
    }))
    
    log.info(`Sold ${hw?.name || hardwareId} for $${sellPrice}`, state.playTime)
    saveGame()
    return true
  },

  canUnlockModel: (index: number) => {
    const model = MODELS[index]
    if (!model) return false
    if (state.unlockedModels.includes(model.id)) return true
    return state.research >= model.researchCost
  },

  unlockModel: (index: number) => {
    const model = MODELS[index]
    if (!model) return false
    
    // If already unlocked, just switch to it
    if (state.unlockedModels.includes(model.id)) {
      setState('currentModelIndex', index)
      log.info(`Switched to training ${model.name}`, state.playTime)
      saveGame()
      return true
    }
    
    if (state.research < model.researchCost) return false

    setState(produce(s => {
      s.research -= model.researchCost
      s.unlockedModels.push(model.id)
      s.currentModelIndex = index
      // Initialize new model at 0% quality, 0 users
      s.models[model.id] = { quality: 0, users: 0 }
    }))
    
    // Track model unlock
    Analytics.modelUnlocked(model.id, state.playTime)
    
    const maxUsersFormatted = model.maxUsers === Infinity ? 'unlimited' : formatNumber(model.maxUsers)
    log.milestone(`Unlocked ${model.name}! Scales to ${maxUsersFormatted} users. Train it to attract users!`, state.playTime)
    checkTutorials(state)
    saveGame()
    return true
  },

  tick: (deltaSeconds: number) => {
    // Don't run game loop when paused
    if (state.paused) return
    
    setState(produce(s => {
      s.playTime += deltaSeconds

      // 1. Energy regenerates up to max
      const currentMaxEnergy = maxEnergy()
      s.energy = Math.min(currentMaxEnergy, s.energy + energyRegen() * deltaSeconds)

      // 2. All owned HW converts energy → compute
      const hwPower = totalHwPower()
      const hwEnergyUse = totalHwEnergyUse()
      const currentMaxCompute = maxCompute()
      const currentEnergyRegen = energyRegen()
      const energyNeeded = hwEnergyUse * deltaSeconds
      
      if (s.compute < currentMaxCompute) {
        if (s.energy >= energyNeeded) {
          // Full power - enough energy for all hardware
          s.energy -= energyNeeded
          s.compute = Math.min(currentMaxCompute, s.compute + hwPower * deltaSeconds)
        } else if (s.energy > 0) {
          // Partial power - but don't drain below a sustainable level
          // Keep a reserve equal to energy regen rate to prevent deadlock
          const energyReserve = currentEnergyRegen * deltaSeconds
          const availableEnergy = Math.max(0, s.energy - energyReserve)
          
          if (availableEnergy > 0) {
            const ratio = availableEnergy / energyNeeded
            s.compute = Math.min(currentMaxCompute, s.compute + hwPower * ratio * deltaSeconds)
            s.energy -= availableEnergy
          }
        }
      }

      // 3. User dynamics for each model
      // First, handle discontinuation migration (if active)
      if (s.discontinuationActiveFrom) {
        const discontinuationModelIndex = MODELS.findIndex(m => m.id === s.discontinuationActiveFrom)
        
        // Find all discontinued models (before discontinuation model) that have users
        const discontinuedModels: string[] = []
        let totalMigratingUsers = 0
        
        for (const modelId of s.unlockedModels) {
          const modelIndex = MODELS.findIndex(m => m.id === modelId)
          if (modelIndex < discontinuationModelIndex && modelIndex !== -1) {
            const modelState = s.models[modelId]
            if (modelState && modelState.users > 0) {
              discontinuedModels.push(modelId)
              // Migration rate: lose users over ~5 minutes (300 seconds)
              // Rate = total_users / 300 per second
              const migrationRate = modelState.users / 300
              const usersLeaving = migrationRate * deltaSeconds
              totalMigratingUsers += usersLeaving
              modelState.users = Math.max(0, modelState.users - usersLeaving)
            }
          }
        }
        
        // Distribute migrating users to the discontinuation model and newer models
        if (totalMigratingUsers > 0) {
          // Find models that can receive users (discontinuation model and newer)
          const receivingModels: string[] = []
          for (const modelId of s.unlockedModels) {
            const modelIndex = MODELS.findIndex(m => m.id === modelId)
            if (modelIndex >= discontinuationModelIndex) {
              const modelDef = MODELS.find(m => m.id === modelId)
              const modelState = s.models[modelId]
              // Only add users to models with quality >= 40 and below max users
              if (modelDef && modelState && modelState.quality >= BALANCE.MIN_QUALITY_FOR_USERS && modelState.users < modelDef.maxUsers) {
                receivingModels.push(modelId)
              }
            }
          }
          
          // Distribute users proportionally to receiving models (prioritize newer models)
          if (receivingModels.length > 0) {
            const usersPerModel = totalMigratingUsers / receivingModels.length
            for (const modelId of receivingModels) {
              const modelDef = MODELS.find(m => m.id === modelId)
              const modelState = s.models[modelId]
              if (modelDef && modelState) {
                modelState.users = Math.min(modelDef.maxUsers, modelState.users + usersPerModel)
              }
            }
          }
        }
      }
      
      // Regular user dynamics for each model
      for (const modelId of s.unlockedModels) {
        const modelDef = MODELS.find(m => m.id === modelId)
        if (!modelDef) continue
        
        if (!s.models[modelId]) {
          s.models[modelId] = { quality: 0, users: 0 }
        }
        
        const modelState = s.models[modelId]

        // Users arrive based on quality, price vs market, and model's growth multiplier
        if (modelState.quality >= BALANCE.MIN_QUALITY_FOR_USERS) {
          const qualityFactor = modelState.quality / 100
          
          // Price comparison: marketPrice / yourPrice
          // If you're cheaper than market, priceFactor > 1 (more users)
          // If you're more expensive, priceFactor < 1 (fewer users)
          const marketPrice = modelDef.marketPricePerMillion
          const priceFactor = Math.min(2, Math.max(0.1, marketPrice / s.price))
          
          // Use the model's growth multiplier instead of index-based calculation
          const growthMultiplier = modelDef.userGrowthMultiplier || 1
          const arrivalRate = BALANCE.BASE_USER_ARRIVAL_RATE * qualityFactor * priceFactor * growthMultiplier
          modelState.users += arrivalRate * deltaSeconds
          
          // Users leave faster if price is much higher than market
          if (s.price > marketPrice * 1.5) {
            const priceLeaveRate = ((s.price / marketPrice) - 1) * 0.1 * modelState.users
            modelState.users = Math.max(0, modelState.users - priceLeaveRate * deltaSeconds)
          }
        } else {
          // Quality too low - users leave
          modelState.users = Math.max(0, modelState.users - BALANCE.USER_LEAVE_RATE * modelState.users * deltaSeconds)
        }

        // Cap users at model's max
        modelState.users = Math.min(modelState.users, modelDef.maxUsers)
      }
      
      // 4. Auto-inference (from self-service upgrades)
      // Limited by both compute AND energy to prevent resource starvation
      const maxAutoRate = autoInferenceRate()
      const throttle = s.autoInferenceThrottle / 100
      const autoRate = maxAutoRate * throttle
      const totalU = Object.values(s.models).reduce((sum, m) => sum + (m?.users || 0), 0)
      
      if (autoRate > 0 && totalU >= 1) {
        const desiredInferences = autoRate * deltaSeconds
        const computePerInference = BALANCE.INFERENCE_COMPUTE_COST
        const energyPerInference = BALANCE.INFERENCE_ENERGY_COST
        
        // Limit by available compute (leave 20% buffer for manual actions)
        const availableCompute = Math.max(0, s.compute * 0.8)
        const maxByCompute = computePerInference > 0 ? availableCompute / computePerInference : Infinity
        
        // Limit by available energy (leave buffer equal to regen rate to prevent deadlock)
        const currentEnergyRegen = energyRegen()
        const energyReserve = currentEnergyRegen * deltaSeconds * 2 // Keep 2x regen as buffer
        const availableEnergy = Math.max(0, s.energy - energyReserve)
        const maxByEnergy = energyPerInference > 0 ? availableEnergy / energyPerInference : Infinity
        
        // Check if compute is limited by energy
        const isEnergyLimited = maxByEnergy < maxByCompute && maxByEnergy < desiredInferences
        if (isEnergyLimited && !computeLimitedWarningShown && maxAutoRate > 0) {
          computeLimitedWarningShown = true
          log.warning('Auto-inference limited by energy. Increase energy regen or reduce auto-inference rate.', s.playTime)
        }
        
        const actualInferences = Math.min(desiredInferences, maxByCompute, maxByEnergy)
        
        if (actualInferences > 0) {
          const computeUsed = actualInferences * computePerInference
          const energyUsed = actualInferences * energyPerInference
          
          s.compute -= computeUsed
          s.energy -= energyUsed
          s.totalComputeSpent += computeUsed
          
          // Calculate revenue from ALL unlocked models
          let totalRevenue = 0
          for (const modelId of s.unlockedModels) {
            const modelState = s.models[modelId]
            const modelDef = MODELS.find(m => m.id === modelId)
            if (modelState && modelDef && modelState.users > 0) {
              const qualityMultiplier = modelState.quality < BALANCE.CRITICAL_QUALITY ? 0.5 : 1
              const revenuePerUser = (s.price * modelDef.tokensPerTask / 1_000_000) * qualityMultiplier
              totalRevenue += revenuePerUser * modelState.users
            }
          }
          s.money += totalRevenue * actualInferences
          s.totalInferences += actualInferences
        }
      }
      
      // 5. Auto-train
      const maxAutoTrainRate = autoTrainRate()
      const trainThrottle = s.autoTrainThrottle / 100
      const autoTrainRateActual = maxAutoTrainRate * trainThrottle
      
      if (autoTrainRateActual > 0) {
        const model = MODELS[s.currentModelIndex]
        const modelState = s.models[model.id]
        
        if (modelState) {
          const desiredTrains = autoTrainRateActual * deltaSeconds
          const computePerTrain = model.trainComputeCost
          const energyPerTrain = BALANCE.TRAIN_ENERGY_COST
          
          // Limit by available compute (leave 20% buffer)
          const availableComputeTrain = Math.max(0, s.compute * 0.8)
          const maxByComputeTrain = computePerTrain > 0 ? availableComputeTrain / computePerTrain : Infinity
          
          // Limit by available energy
          const currentEnergyRegenTrain = energyRegen()
          const energyReserveTrain = currentEnergyRegenTrain * deltaSeconds * 2
          const availableEnergyTrain = Math.max(0, s.energy - energyReserveTrain)
          const maxByEnergyTrain = energyPerTrain > 0 ? availableEnergyTrain / energyPerTrain : Infinity
          
          const actualTrains = Math.min(desiredTrains, maxByComputeTrain, maxByEnergyTrain)
          
          if (actualTrains > 0) {
            const computeUsedTrain = actualTrains * computePerTrain
            const energyUsedTrain = actualTrains * energyPerTrain
            
            s.compute -= computeUsedTrain
            s.energy -= energyUsedTrain
            s.totalComputeSpent += computeUsedTrain
            
            // Apply training effect (same logic as clickTrain)
            const efficiency = trainEfficiency()
            const baseGain = BALANCE.BASE_QUALITY_PER_TRAIN * efficiency * actualTrains
            const currentQuality = modelState.quality
            const maxQuality = model.maxQuality
            const qualityRoom = maxQuality - currentQuality
            const asymptoticGain = baseGain * (qualityRoom / maxQuality)
            s.models[model.id].quality = Math.min(maxQuality, currentQuality + asymptoticGain)
          }
        }
      }
      
      // 6. Auto-research
      const maxAutoResearchRate = autoResearchRate()
      const researchThrottle = s.autoResearchThrottle / 100
      const autoResearchRateActual = maxAutoResearchRate * researchThrottle
      
      if (autoResearchRateActual > 0 && s.totalComputeSpent >= BALANCE.RESEARCH_UNLOCK_THRESHOLD) {
        const desiredResearch = autoResearchRateActual * deltaSeconds
        const computePerResearch = researchComputeCost()
        const energyPerResearch = researchEnergyCost()
        
        // Limit by available compute (leave 20% buffer)
        const availableComputeRes = Math.max(0, s.compute * 0.8)
        const maxByComputeRes = computePerResearch > 0 ? availableComputeRes / computePerResearch : Infinity
        
        // Limit by available energy
        const currentEnergyRegenRes = energyRegen()
        const energyReserveRes = currentEnergyRegenRes * deltaSeconds * 2
        const availableEnergyRes = Math.max(0, s.energy - energyReserveRes)
        const maxByEnergyRes = energyPerResearch > 0 ? availableEnergyRes / energyPerResearch : Infinity
        
        const actualResearch = Math.min(desiredResearch, maxByComputeRes, maxByEnergyRes)
        
        if (actualResearch > 0) {
          const computeUsedRes = actualResearch * computePerResearch
          const energyUsedRes = actualResearch * energyPerResearch
          
          s.compute -= computeUsedRes
          s.energy -= energyUsedRes
          s.totalComputeSpent += computeUsedRes
          
          const efficiency = researchEfficiency()
          s.research += actualResearch * efficiency
        }
      }
      
      // Check for tutorial messages
      checkTutorials(s)
    }))
  },

  reset: () => {
    setState({ ...initialState, models: createInitialModels(), ownedHardware: ['laptop'], startTime: Date.now() })
    eventLog.clear()
    log.info('Welcome to GPT Clicker! Train your model to begin.', 0)
    SaveSystem.clear()
  },

  save: () => {
    return JSON.stringify(unwrap(state))
  },

  load: (saveString: string) => {
    try {
      const data = JSON.parse(saveString)
      
      // Migration: if price is in old format (per inference), convert to $/1M tokens
      if (data.price && data.price < 1) {
        // Old price was per inference, new is per 1M tokens
        // Reset to market price of first unlocked model
        data.price = BALANCE.START_PRICE
      }
      
      setState({ ...initialState, ...data })
      return true
    } catch {
      return false
    }
  },

  setDebugEra: (era: DebugEra) => {
    // Build new models object
    const newModels: Record<string, ModelState> = {}
    for (const modelId of era.unlockedModels) {
      const modelDef = MODELS.find(m => m.id === modelId)
      if (modelDef) {
        newModels[modelId] = { 
          quality: modelDef.maxQuality * 0.8,
          users: Math.min(modelDef.maxUsers * 0.1, 1000000)
        }
      }
    }
    
    // Find current model index
    const latestModelId = era.unlockedModels[era.unlockedModels.length - 1]
    const modelIndex = MODELS.findIndex(m => m.id === latestModelId)
    
    // Calculate upgrades needed for max energy/compute caps
    const newUpgrades: Record<string, number> = {}
    
    // Max energy: better-outlet gives +50, starts at 100
    const energyNeeded = era.maxEnergy - BALANCE.START_MAX_ENERGY
    if (energyNeeded > 0) {
      newUpgrades['better-outlet'] = Math.ceil(energyNeeded / 50)
    }
    
    // Max compute: ram-upgrade gives +50, ssd-array gives +200, starts at 50
    const computeNeeded = era.maxCompute - BALANCE.START_MAX_COMPUTE
    if (computeNeeded > 0) {
      // Use ssd-array for bulk (+200 each), ram-upgrade for remainder (+50 each)
      const ssdCount = Math.floor(computeNeeded / 200)
      const remainder = computeNeeded - (ssdCount * 200)
      const ramCount = Math.ceil(remainder / 50)
      if (ssdCount > 0) newUpgrades['ssd-array'] = ssdCount
      if (ramCount > 0) newUpgrades['ram-upgrade'] = ramCount
    }
    
    // Energy regen: calculate based on hardware energy consumption
    // We need enough regen to power all the hardware
    const hwEnergyConsumption = era.ownedHardware.reduce((sum, hwId) => {
      const hw = HARDWARE.find(h => h.id === hwId)
      return sum + (hw?.energyPerSec || 0)
    }, 0)
    
    // Give 50% extra regen buffer above hardware needs (for training/research actions)
    const targetRegen = Math.ceil(hwEnergyConsumption * 1.5)
    let regenNeeded = targetRegen - BALANCE.START_ENERGY_REGEN
    
    if (regenNeeded > 0) {
      // Use highest tier upgrades first, then work down
      // antimatter (+10000), dyson (+1000), fusion (+100), nuclear (+20), power-grid (+5), solar (+1)
      
      const antimatterCount = Math.floor(regenNeeded / 10000)
      regenNeeded -= antimatterCount * 10000
      
      const dysonCount = Math.floor(regenNeeded / 1000)
      regenNeeded -= dysonCount * 1000
      
      const fusionCount = Math.floor(regenNeeded / 100)
      regenNeeded -= fusionCount * 100
      
      const nuclearCount = Math.floor(regenNeeded / 20)
      regenNeeded -= nuclearCount * 20
      
      const gridCount = Math.floor(regenNeeded / 5)
      regenNeeded -= gridCount * 5
      
      const solarCount = Math.ceil(regenNeeded)
      
      if (antimatterCount > 0) newUpgrades['antimatter-reactor'] = antimatterCount
      if (dysonCount > 0) newUpgrades['dyson-sphere'] = dysonCount
      if (fusionCount > 0) newUpgrades['fusion-reactor'] = fusionCount
      if (nuclearCount > 0) newUpgrades['nuclear-plant'] = nuclearCount
      if (gridCount > 0) newUpgrades['power-grid'] = gridCount
      if (solarCount > 0) newUpgrades['solar-panel'] = solarCount
    }
    
    // Set all at once for proper reactivity
    setState({
      money: era.money,
      energy: era.energy,
      compute: era.compute,
      research: era.research,
      unlockedModels: [...era.unlockedModels],
      ownedHardware: [...era.ownedHardware],
      currentModelIndex: modelIndex >= 0 ? modelIndex : 0,
      models: newModels,
      upgrades: newUpgrades,
    })
    
    log.info(`DEBUG: Jumped to ${era.name}`, state.playTime)
    saveGame()
  }
}

// Helper for formatting in logs
function formatNumber(n: number): string {
  if (n < 1000) return n.toFixed(0)
  if (n < 1_000_000) return (n / 1000).toFixed(0) + 'K'
  if (n < 1_000_000_000) return (n / 1_000_000).toFixed(0) + 'M'
  return (n / 1_000_000_000).toFixed(0) + 'B'
}

// Tutorial/Narrative system - tracks which messages have been shown
const shownTutorials = new Set<string>()

function showTutorial(id: string, message: string, type: 'info' | 'milestone' = 'info', time: number = 0) {
  if (shownTutorials.has(id)) return
  shownTutorials.add(id)
  
  // Track milestones in analytics
  if (type === 'milestone') {
    Analytics.milestone(id, time)
    log.milestone(message, time)
    // Show toast for milestones with share text
    toast.milestone(message, message)
  } else {
    log.info(message, time)
  }
}

// Check and trigger tutorial messages based on game state
function checkTutorials(s: GameState) {
  const time = s.playTime
  const firstModelQuality = s.models['tiny']?.quality || 0
  const firstModelUsers = s.models['tiny']?.users || 0
  
  // Opening sequence
  if (s.totalComputeSpent === 0 && s.playTime < 1) {
    showTutorial('welcome', 'Year 2020. You have an idea: train a small language model based on the transformer architecture.', 'info', time)
  }
  
  // First train
  if (s.totalComputeSpent > 0 && s.totalComputeSpent <= 15) {
    showTutorial('first-train', 'Training started! Your laptop converts energy into compute. Each training run improves model quality.', 'info', time)
  }
  
  // Quality milestones
  if (firstModelQuality >= 20 && firstModelQuality < 40) {
    showTutorial('quality-20', 'Model is learning basic patterns. Keep training to improve quality.', 'info', time)
  }
  
  if (firstModelQuality >= 40) {
    showTutorial('quality-40', 'Quality above 40%! Your model can now generate somewhat coherent text. Users are starting to notice...', 'milestone', time)
  }
  
  if (firstModelQuality >= 50) {
    showTutorial('quality-50', 'Upgrades unlocked! Use your earnings to buy better hardware and expand your energy capacity.', 'milestone', time)
  }
  
  // First users
  if (firstModelUsers >= 1 && firstModelUsers < 5) {
    showTutorial('first-users', 'Your first users! Click INFERENCE to serve their requests and earn money.', 'milestone', time)
  }
  
  if (firstModelUsers >= 10) {
    showTutorial('users-10', 'Word is spreading. More users are trying your model. Consider raising the price...', 'info', time)
  }
  
  // Money milestones
  if (s.money >= 100) {
    showTutorial('money-100', 'You have $100! Time to upgrade your hardware. Better GPUs = more compute = faster training.', 'info', time)
  }
  
  if (s.money >= 1000) {
    showTutorial('money-1k', 'Your little AI project is becoming a real business. Investors are starting to call...', 'info', time)
  }
  
  // Research unlock
  if (s.totalComputeSpent >= BALANCE.RESEARCH_UNLOCK_THRESHOLD && s.research === 0) {
    showTutorial('research-unlock', 'RESEARCH unlocked! Spend compute on research to unlock bigger, more powerful models.', 'milestone', time)
  }
  
  // First research
  if (s.research >= 1 && s.research < 10) {
    showTutorial('first-research', 'Research points accumulate. When you have enough, you can unlock the next model architecture.', 'info', time)
  }
  
  // Model unlocks
  if (s.unlockedModels.includes('gpt2-small') && !s.unlockedModels.includes('gpt2-large')) {
    showTutorial('gpt2-small', 'GPT-2 Small unlocked! This architecture can handle more complex tasks. Train it from scratch.', 'milestone', time)
  }
  
  if (s.unlockedModels.includes('gpt2-large')) {
    showTutorial('gpt2-large', 'GPT-2 Large! People are saying this might be "too dangerous to release"...', 'milestone', time)
  }
  
  if (s.unlockedModels.includes('gpt3')) {
    showTutorial('gpt3', 'GPT-3 era begins. Few-shot learning changes everything. The world is watching.', 'milestone', time)
  }
  
  if (s.unlockedModels.includes('gpt4')) {
    showTutorial('gpt4', 'GPT-4 is here. Multimodal, reasoning, and scarily capable. The AI race intensifies.', 'milestone', time)
  }
  
  // LLM Crisis messages - when models reach usable quality but feel stagnant
  const u1State = s.models['u1']
  if (u1State && u1State.quality >= 40) {
    showTutorial('u1-crisis', 'Users are complaining: "U-1 feels the same as before." Scaling laws are hitting diminishing returns. The LLM plateau is real.', 'info', time)
  }
  
  const gpt5State = s.models['gpt5']
  if (gpt5State && gpt5State.quality >= 40) {
    showTutorial('gpt5-crisis', 'Critics everywhere: "GPT-5 is just GPT-4 with more parameters." The bitter lesson of scaling has limits. Time to find a new paradigm...', 'info', time)
  }
  
  if (s.unlockedModels.includes('agi')) {
    showTutorial('agi', 'You did it. AGI is here. It understands. It improves itself. What happens next is up to you...', 'milestone', time)
  }
  
  // Hardware milestones
  if (s.ownedHardware.includes('gaming-gpu')) {
    showTutorial('hw-gaming', 'Nice GPU! Training is much faster now. Your neighbors might complain about the electricity bill.', 'info', time)
  }
  
  if (s.ownedHardware.includes('server-rack')) {
    showTutorial('hw-server', 'A real server rack! You\'ve moved beyond the garage. Time to rent some office space.', 'info', time)
  }
  
  if (s.ownedHardware.includes('datacenter')) {
    showTutorial('hw-datacenter', 'Your own datacenter! You\'re now competing with the big tech companies.', 'milestone', time)
  }
  
  if (s.ownedHardware.includes('satellite')) {
    showTutorial('hw-satellite', 'Orbital compute! The Moonlink constellation provides unprecedented processing power from space.', 'milestone', time)
  }
  
  if (s.ownedHardware.includes('quantum')) {
    showTutorial('hw-quantum', 'Quantum supremacy achieved. The QC-1024 operates on principles that would make Einstein nervous.', 'milestone', time)
  }
  
  // User milestones
  const totalU = Object.values(s.models).reduce((sum, m) => sum + (m?.users || 0), 0)
  
  if (totalU >= 100) {
    showTutorial('users-100', '100 users! You\'re gaining traction. Tech blogs are writing about you.', 'info', time)
  }
  
  if (totalU >= 1000) {
    showTutorial('users-1k', '1,000 users! You need to hire a support team. And maybe some lawyers.', 'info', time)
  }
  
  if (totalU >= 100000) {
    showTutorial('users-100k', '100K users! You\'re a unicorn startup now. VCs are fighting to give you money.', 'milestone', time)
  }
  
  if (totalU >= 1000000) {
    showTutorial('users-1m', 'ONE MILLION USERS! You\'re reshaping how humanity interacts with technology.', 'milestone', time)
  }
}

export { state }
